import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { usePage } from "@/contexts/PageContext";
import { CheckoutFlow } from "./CheckoutFlow";
import { CheckoutSuccessPage } from "./checkout/CheckoutSuccessPage";

export function CheckoutRoute() {
  const { checkoutId, step } = useParams<{ checkoutId: string; step: string }>();
  const { setStripe, setCheckoutSettings } = usePage();
  const [data, setData] = useState<{
    products: any[];
    lineItems: Array<{ productId: string; variantId?: string }>;
    stripe: { publishableKey: string; accountId?: string } | null;
    checkoutSettings: any;
    artistName?: string;
    onCheckoutSuccess?: { type: "GoTo"; route: string };
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!checkoutId) return;

    const fetchCheckout = async () => {
      try {
        const response = await fetch(`/api/checkout/${checkoutId}`);
        if (response.ok) {
          const result = await response.json();
          setData({
            products: result.products || [],
            lineItems: result.checkout?.lineItems || [],
            stripe: result.stripe || null,
            checkoutSettings: result.checkoutSettings || null,
            artistName: result.artistName,
            onCheckoutSuccess: result.checkout?.onCheckoutSuccess,
          });
          if (result.stripe) {
            setStripe(result.stripe);
          }
          if (result.checkoutSettings) {
            setCheckoutSettings(result.checkoutSettings);
          }
        }
      } catch (error) {
        console.error("Error fetching checkout:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCheckout();
  }, [checkoutId, setStripe, setCheckoutSettings]);

  // Redirect /checkout/:checkoutId to /checkout/:checkoutId/email
  if (checkoutId && !step) {
    return <Navigate to={`/checkout/${checkoutId}/email`} replace />;
  }

  if (step === "success") {
    if (loading || !data || !checkoutId) {
      return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    }
    return <CheckoutSuccessPage onCheckoutSuccess={data.onCheckoutSuccess} />;
  }

  if (loading || !data || !checkoutId) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const validSteps = ["email", "shipping", "payment"];
  if (step && !validSteps.includes(step)) {
    return <Navigate to={`/checkout/${checkoutId}/email`} replace />;
  }

  return (
    <CheckoutFlow
      checkoutId={checkoutId}
      products={data.products}
      lineItems={data.lineItems}
      stripe={data.stripe}
      checkoutSettings={data.checkoutSettings}
      artistName={data.artistName}
    />
  );
}
